import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Link,
  IconButton,
  InputAdornment,
  Divider,
  Avatar,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import { signInWithPopup } from "firebase/auth";

// Replace with your croissant/mascot image
import CroissantImg from "../assets/Full_icon.png";

function Signup() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Email/password signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Update displayName with username
      await updateProfile(userCredential.user, { displayName: username });
      navigate("/"); // redirect straight to protected home/dashboard
    } catch (err) {
      // inside your catch block
if (err.code === "auth/email-already-in-use") {
  setError(
    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
      This Email is already in use.{" "} Do you want to {" "}
      <Link
        component={RouterLink}
        to="/login"
        underline="none"
        sx={{ fontWeight: 600, color: "#2563eb" }}
      >
       Login?
      </Link>
    </Typography>
  );
} else {
  setError(
    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
      {err.message}
    </Typography>
  );
}

    } finally {
      setLoading(false);
    }
  };

  // Google signup
  const handleGoogleSignup = async () => {
    setError("");
    try {
      setLoading(true);
      await signInWithPopup(auth, googleProvider);
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="center" sx={{ mt: 8 }}>
      <Box position="relative" width={440}>
        {/* Circular icon overlapping the card */}
        <Box
          sx={{
            position: "absolute",
            top: -40,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            bgcolor: "#f9fafb",
            width: 80,
            height: 80,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <Avatar
            src={CroissantImg}
            alt="Croissant"
            sx={{ width: 48, height: 48, bgcolor: "transparent" }}
          />
        </Box>

        <Card
          sx={{
            borderRadius: 4,
            boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
            bgcolor: "#fff",
            pt: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box textAlign="center" mb={3}>
              <Typography variant="h6" fontWeight={700}>
                Create Account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign up to get started
              </Typography>
            </Box>

            {/* Google button */}
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleSignup}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderColor: "#e5e7eb",
                color: "#111827",
                bgcolor: "#fff",
                borderRadius: 2,
                "&:hover": {
                  bgcolor: "#f9fafb",
                  borderColor: "#d1d5db",
                },
              }}
              startIcon={
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    background: `
                      conic-gradient(
                        #4285F4 0deg 90deg,
                        #34A853 90deg 180deg,
                        #FBBC05 180deg 270deg,
                        #EA4335 270deg 360deg
                      )
                    `,
                    color: "#fff",
                  }}
                >
                  G
                </Box>
              }
            >
              Continue with Google
            </Button>

            <Divider sx={{ my: 2 }}>Or</Divider>

            {/* Signup form */}
            <Box component="form" onSubmit={handleSignup}>
              <TextField
                placeholder="Enter your username"
                type="text"
                fullWidth
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ color: "#6b7280" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              <TextField
                placeholder="Enter your email"
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: "#6b7280" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              <TextField
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: "#6b7280" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />

              {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  bgcolor: "#2563eb",
                  "&:hover": { bgcolor: "#1d4ed8" },
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : "Sign Up"}
              </Button>
            </Box>

            <Typography variant="body2" textAlign="center" mt={2} color="text.secondary">
              Already have an account?{" "}
              <Link
                component={RouterLink}
                to="/login"
                underline="none"
                fontWeight={700}
                sx={{ color: "#2563eb" }}
              >
                Sign In
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Signup;
